import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Card, CardBody, Row, Col, Input, Label, FormGroup, Button } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { getSponsor, saveSponsorMaster } from "../../services/Hrms/hrms"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import "../Tenancy/tenancy.css"

const EMIRATES = ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"]
const blank = {
  code: "", sponsored_by: "", sponsor_type: "individual", nationality: "", country: "United Arab Emirates",
  emirate: "", address: "", license_no: "", national_id: "", national_id_expiry: "",
  passport_no: "", passport_expiry: "", contact_phone: "", contact_email: "", notes: "", status: 1,
}

/**
 * Administration → Sponsors → New / Edit (separate page, StaffFlow parity).
 */
const SponsorForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  document.title = `${isEdit ? "Edit" : "New"} Sponsor | Calyvern`
  const [form, setForm] = useState(blank)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isEdit) return
    ;(async () => {
      try {
        const r = await getSponsor(id)
        const d = r?.data
        if (d) setForm({ ...blank, ...d, status: d.status ?? 1 })
      } catch (e) { toastError(e?.message) }
    })()
  }, [id, isEdit])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const save = async () => {
    if (!form.sponsored_by?.trim()) return toastError("Name is required")
    try {
      setSaving(true)
      const res = await saveSponsorMaster(isEdit ? { ...form, id } : form)
      toastSuccess(res?.message || "Sponsor saved")
      navigate("/admin/sponsors")
    } catch (e) { toastError(e?.message) } finally { setSaving(false) }
  }

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <div className="sticky-top w-100" style={{ zIndex: 5 }}>
          <div className="d-flex justify-content-between align-items-center me-2">
            <Breadcrumbs title="Administration" breadcrumbObject={[
              { title: "Administration", link: "#" },
              { title: "Sponsors", link: "/admin/sponsors" },
              { title: isEdit ? "Edit sponsor" : "New sponsor", link: "#" },
            ]} />
            <div className="mb-3">
              <Button color="light" className="me-2" onClick={() => navigate("/admin/sponsors")}>Cancel</Button>
              <Button color="primary" onClick={save} disabled={saving}>{saving ? "Saving…" : isEdit ? "Update" : "Save"}</Button>
            </div>
          </div>
        </div>

        <Card className="tnt-page-card me-2 px-2 pb-2 main-dashboard-bg"><CardBody>
          <h6 className="text-uppercase fw-bold mb-3">Sponsor details</h6>
          <Row>
            <Col md={6}><FormGroup><Label>Code</Label><Input bsSize="sm" placeholder="Auto; editable" value={form.code || ""} onChange={e => set("code", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Name<span className="mandatory">*</span></Label><Input bsSize="sm" value={form.sponsored_by} onChange={e => set("sponsored_by", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Sponsor type</Label>
              <Input type="select" bsSize="sm" value={form.sponsor_type} onChange={e => set("sponsor_type", e.target.value)}>
                <option value="individual">Individual (local partner)</option>
                <option value="corporate">Corporate (sponsoring company)</option>
              </Input></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Nationality</Label><Input bsSize="sm" value={form.nationality} onChange={e => set("nationality", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Country</Label><Input bsSize="sm" value={form.country} onChange={e => set("country", e.target.value)} /></FormGroup></Col>
            {/^united arab emirates$/i.test(form.country || "") && (
              <Col md={6}><FormGroup><Label>Emirate</Label>
                <Input type="select" bsSize="sm" value={form.emirate} onChange={e => set("emirate", e.target.value)}>
                  <option value="">Select emirate</option>{EMIRATES.map(em => <option key={em} value={em}>{em}</option>)}
                </Input></FormGroup></Col>
            )}
            <Col md={6}><FormGroup><Label>Phone</Label><Input bsSize="sm" value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Email</Label><Input type="email" bsSize="sm" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} /></FormGroup></Col>
            <Col md={12}><FormGroup><Label>Address</Label><Input bsSize="sm" value={form.address} onChange={e => set("address", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Trade licence no.</Label><Input bsSize="sm" value={form.license_no} onChange={e => set("license_no", e.target.value)} /></FormGroup></Col>
            <Col md={6} />
            <Col md={6}><FormGroup><Label>National ID No.</Label><Input bsSize="sm" value={form.national_id} onChange={e => set("national_id", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>National ID expiry</Label><Input type="date" bsSize="sm" value={(form.national_id_expiry || "").slice(0, 10)} onChange={e => set("national_id_expiry", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Passport No.</Label><Input bsSize="sm" value={form.passport_no} onChange={e => set("passport_no", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Passport expiry</Label><Input type="date" bsSize="sm" value={(form.passport_expiry || "").slice(0, 10)} onChange={e => set("passport_expiry", e.target.value)} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Active</Label>
              <Input type="select" bsSize="sm" value={form.status} onChange={e => set("status", Number(e.target.value))}>
                <option value={1}>Active</option><option value={0}>Inactive</option>
              </Input></FormGroup></Col>
            <Col md={12}><FormGroup><Label>Notes</Label><Input type="textarea" rows={2} bsSize="sm" value={form.notes} onChange={e => set("notes", e.target.value)} /></FormGroup></Col>
          </Row>
        </CardBody></Card>

        <Card className="me-2 px-2 pb-2"><CardBody>
          <h6 className="text-uppercase fw-bold mb-1">Shareholding</h6>
          <div className="text-muted small">Link this sponsor to companies and set share % on each company's <b>Details → Shareholders / Sponsors</b> section.</div>
        </CardBody></Card>
      </div>
    </div>
  )
}

export default SponsorForm
